import { recalcInvoice, getGasPlantVolumesStarting, getGasPlantVolumesEnding, getGasContainerTrailer, getGasTrailerOptions } from "../../../../services/requests/billing/gas-billing-requests";
import { FormItemType } from "../../../UI/view/component-map";
import { DataSource } from "../../../UI/view/data-source";
import { FormView } from "../../../UI/view/form-view";
import { connectView } from "../../../UI/view/view";
import { billingPermissions } from "../billing-datasource";
import { gasBillingOrderDeliveryDataSource } from "./gas-billing-datasource";

// Dedicated create form for the legacy Orders "Add Gas Billing Entry" flow. It is
// a superset of the pending-delivery create form (gas-billing-create-invoice.ts):
// it adds Tube By Tube entry (Bank dropdown + add/remove rows), editable Date/Time
// In/Out, and the read-only customer Billing Instructions. Kept separate so the
// pending-delivery -> create screen is unchanged.

const roundTo = (value: number, places: number) => {
  const f = Math.pow(10, places);
  return Math.sign(value) * Math.round(Math.abs(value) * f) / f;
};

// Mirrors dbo.GasVolume: psig decimal(38,2), temperature/water decimal(38,4), He decimal(14,4), returns decimal(38,2).
const gasVolume = (psig: number, temperature: number, waterVolume: number, heliumPercentage: number = 1) => {
  psig = roundTo(psig, 2);
  temperature = roundTo(temperature, 4);
  waterVolume = roundTo(waterVolume, 4);
  heliumPercentage = roundTo(heliumPercentage, 4);
  const waterVolumeAtPT = waterVolume * (1 + (7.4e-7 * psig));
  const volume = (
    ((psig + 14.7) / 14.7) * (529.7 / (temperature + 459.67))
    * (1.00049 / (1 + ((10.2297e-5 - (19.2998e-8 * (temperature + 459.67)) + (1.1836e-10 * Math.pow(temperature + 459.67, 2))) * (psig + 14.7)
      - (2.217e-10 * Math.pow(psig + 14.7, 2)))))
  ) * waterVolumeAtPT;
  return roundTo(volume * heliumPercentage, 2);
};

// Bank options: the order-page modal saves a single CT row or trailer tubes A-M.
const bankOptions = [
  { id: null, text: '' },
  { id: 'A', text: 'A' }, { id: 'B', text: 'B' }, { id: 'C', text: 'C' },
  { id: 'D', text: 'D' }, { id: 'E', text: 'E' }, { id: 'F', text: 'F' },
  { id: 'G', text: 'G' }, { id: 'H', text: 'H' }, { id: 'I', text: 'I' },
  { id: 'J', text: 'J' }, { id: 'K', text: 'K' }, { id: 'L', text: 'L' },
  { id: 'M', text: 'M' }, { id: 'CT', text: 'CT' },
];

// Legacy DoGasBillingCalcs: Wait For Fill nets Out - In, every other delivery type In - Out.
const displayNet = (volStart: number, volEnd: number, getValues: any) =>
  getValues('isWaitForFill') ? volEnd - volStart : volStart - volEnd;

const isTubeRow = (r: any) => !!r?.bank && r.bank !== 'CT' && r.bank !== 'X';

// Legacy tube-by-tube totals (sum of bank start/end volumes and the net).
const updateTubeTotals = (getValues: any, setValue: any) => {
  const tubes = (getValues('gasBillingDetails') ?? []).filter(isTubeRow);
  const start = tubes.reduce((s: number, r: any) => s + Number(r.volumeCalcStart ?? 0), 0);
  const end = tubes.reduce((s: number, r: any) => s + Number(r.volumeCalcEnd ?? 0), 0);
  setValue('tubeVolumeStartTotal', start);
  setValue('tubeVolumeEndTotal', end);
  setValue('tubeNetVolumeTotal', displayNet(start, end, getValues));
};

const applyRecalced = (recalced: any, getValues: any, setValue: any) => {
  (recalced?.gasBillingDetails ?? []).forEach((d: any, i: number) => {
    setValue(`gasBillingDetails[${i}].volumeCalcStart`, d.volumeCalcStart);
    setValue(`gasBillingDetails[${i}].volumeCalcEnd`, d.volumeCalcEnd);
    setValue(`gasBillingDetails[${i}].volumeCalcNet`, displayNet(d.volumeCalcStart ?? 0, d.volumeCalcEnd ?? 0, getValues));
  });
  updateTubeTotals(getValues, setValue);
};

// Recompute a row's calculated start/end/net volumes from its current readings,
// overlaying the just-changed field's value (react-hook-form has not committed it
// to getValues() yet).
const recalcRow = (changed: string, v: any, getValues: any, setValue: any, rowIdentity: any) => {
  const { gasBillingDetails } = getValues();
  const r = { ...gasBillingDetails[rowIdentity], [changed]: v };
  const isTube = isTubeRow(r);
  // Legacy tube rows always calculate (and save) at 100% He.
  const he = isTube || r.hePercentage == null ? 1 : Number(r.hePercentage);
  const volStart = Math.round(gasVolume(Number(r.pressureStarted ?? 0), Number(r.tempStarted ?? 0), Number(r.waterVolumeStarted ?? 0), he) ?? 0);
  const volEnd = Math.round(gasVolume(Number(r.pressureEnded ?? 0), Number(r.tempEnded ?? 0), Number(r.waterVolumeEnded ?? 0), he) ?? 0);
  setValue(`gasBillingDetails[${rowIdentity}].volumeCalcStart`, volStart);
  setValue(`gasBillingDetails[${rowIdentity}].volumeCalcEnd`, volEnd);
  setValue(`gasBillingDetails[${rowIdentity}].volumeCalcNet`, displayNet(volStart, volEnd, getValues));
  updateTubeTotals(getValues, setValue);
};

const readingColumn = (header: string, name: string) => ({
  Header: header,
  accessor: name,
  formItemProps: {
    name,
    onChange: (v: any, getValues: any, setValue: any, editTableProps: any) =>
      recalcRow(name, v, getValues, setValue, editTableProps.rowIdentity),
  },
});

const calcColumn = (header: string, name: string) => ({
  Header: header,
  accessor: name,
  formItemProps: { name, disabled: () => true, className: 'readonly' },
});

// Format a server date (ISO/Date) to the local "YYYY-MM-DDTHH:mm" the datetime
// field stores, without a timezone shift (mirrors DateTimeField.toLocalInput).
const toLocalInput = (value: any) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const plantTracking = (getValues: any) => getValues('plantTrackingNumberId') ?? getValues('trackingNumberId');

const fetchPlantVolumes = (side: 'Started' | 'Ended', getValues: any) => {
  const args = [plantTracking(getValues), getValues('sourceOrderId'), getValues('pageDeliveryTypeId')] as const;
  return side === 'Started' ? getGasPlantVolumesStarting(...args) : getGasPlantVolumesEnding(...args);
};

// Legacy "Use Plant Volumes (Starting)/(Ending)" — apply the pulled plant readings
// to the single-container (row 0) started/ended side + the header container/date,
// then recompute the calculated volumes (legacy DoGasBillingCalcs).
const applyPlantVolumes = async (
  side: 'Started' | 'Ended',
  getValues: any,
  setValue: any,
) => {
  const data = await fetchPlantVolumes(side, getValues);
  // Legacy disables these buttons in the same cases the server returns nothing.
  if (!data) {
    alert(side === 'Started'
      ? 'Use Plant Volumes is not available: the order has not shipped yet, or it is a Domestic Cascade child order. Enter the readings manually.'
      : 'Use Plant Volumes is not available: the order has not shipped yet, it is a Domestic Cascade child order, or not exactly one container was returned by this customer within 5 days of ship. Enter the readings manually.');
    return;
  }
  if (data.hasReadings) {
    if (data.viewAllTrailerOut) setValue('trailerOutViewAll', true);
    setValue(`gasBillingDetails[0].pressure${side}`, data.pressure ?? 0);
    setValue(`gasBillingDetails[0].temp${side}`, data.temperature ?? 0);
    setValue(`gasBillingDetails[0].waterVolume${side}`, data.waterVolume ?? 0);
    if (data.containerId) setValue(side === 'Started' ? 'containerIdstarted' : 'containerIdended', data.containerId);
    if (data.date) setValue(side === 'Started' ? 'dateStarted' : 'dateEnded', toLocalInput(data.date));
  }

  await applyTrailerChange(getValues, setValue);
};

// Legacy hlStartingInfoFillSheet / hlEndingInfoFillSheet (only linked when the matching Use Plant Volumes button is enabled).
const openFillSheet = async (side: 'Started' | 'Ended', getValues: any) => {
  const win = window.open('', '_blank');
  const data = await fetchPlantVolumes(side, getValues);
  if (!data?.fillSheetTrackingNumberId) {
    win?.close();
    alert('No fill sheet is available for this side.');
    return;
  }
  const url = `https://vts.linde.grp/Helium/VTS/NewReports/GasFillSheet.aspx?gtrkno=${data.fillSheetTrackingNumberId}`;
  if (win) win.location.href = url;
  else window.open(url, '_blank');
};

// Legacy trailer dropdown lists; keeps the current value selectable.
const trailerOptions = (field: string, customerOnSite: (getValues: any) => boolean) =>
  async (_data: any, getValues: any) => {
    const trk = getValues ? plantTracking(getValues) : null;
    if (!trk) return [];
    const list: string[] = await getGasTrailerOptions(trk, getValues('sourceOrderId'), customerOnSite(getValues));
    const current = getValues(field);
    const ids = current && !list.includes(current) ? [current, ...list] : list;
    return [{ id: '', text: '-- Select One --' }, ...ids.map((id) => ({ id, text: id }))];
  };

// Legacy TrailerChange (runs for both trailers): CT water volume = trailer WaterVol; tube rows =
// WaterVol / NumberOfTubes when the trailer has tubes. Then legacy DoGasBillingCalcs.
const applyTrailerChange = async (getValues: any, setValue: any) => {
  const rows = getValues('gasBillingDetails') ?? [];
  for (const side of ['Started', 'Ended'] as const) {
    const containerId = getValues(side === 'Started' ? 'containerIdstarted' : 'containerIdended');
    if (!containerId) continue;
    const info = await getGasContainerTrailer(containerId);
    if (!info) continue;
    const waterVol = Number(info.waterVol ?? 0);
    const tubes = Number(info.numberOfTubes ?? 0);
    const perTube = tubes > 0 ? roundTo(waterVol / tubes, 2) : null;
    setValue(side === 'Started' ? 'tubeWaterVolumeStarted' : 'tubeWaterVolumeEnded', perTube);
    if (side === 'Started') {
      setValue('numberOfTubes', tubes);
      setValue('totalWaterVolume', waterVol);
    }
    rows.forEach((r: any, i: number) => {
      if (!isTubeRow(r)) setValue(`gasBillingDetails[${i}].waterVolume${side}`, waterVol);
      else if (perTube != null) setValue(`gasBillingDetails[${i}].waterVolume${side}`, perTube);
    });
  }
  applyRecalced(await recalcInvoice(getValues()), getValues, setValue);
};

export const getOrderReturnPath = (sourceOrderId: unknown) => {
  const orderId = Number(sourceOrderId);
  return Number.isInteger(orderId) && orderId > 0
    ? `/orders/edit/${orderId}#Billing%20Information`
    : '/billing/gasproduct#Invoices';
};

const createFooter = [
  {
    type: FormItemType.submitButton,
    label: 'Create',
    onAfterSubmit: ({ redirect, watch }: any) => {
      alert('Invoice Successfully Created');
      redirect(getOrderReturnPath(watch('sourceOrderId')));
    },
  },
];

export const getGasBillingForOrderCreate = {
  editMode: true,
  pageHeader: 'Gas Billing',
  permissions: billingPermissions,
  breadcrumbs: [
    { name: 'Gas Billing', icon: 'currency-dollar', to: '/billing/gasproduct#Invoices' },
  ],
  items: [
    {
      type: FormItemType.masterDetailLayout,
      items: [
        {
          items: [
            {
              type: FormItemType.infoPane,
              header: ({ gasbillingOrderDelivery }: any) => gasbillingOrderDelivery?.current?.data?.customerName,
              infoListItems: [
                { label: 'Customer', name: 'customerName' },
                { label: 'Customer Id', name: 'customerId' },
              ],
            },
          ],
        },
        {
          type: FormItemType.tab,
          items: [
            {
              label: 'Gas Invoice',
              footerItems: createFooter,
              items: [
                {
                  type: FormItemType.fieldset,
                  label: 'Invoice Details',
                  items: [
                    {
                      type: FormItemType.row,
                      items: [
                        { label: 'Shipping Order #', name: 'socpo', disabled: () => true },
                        {
                          label: 'Tracking #',
                          name: 'trackingNumberId',
                          type: FormItemType.labelField,
                          link: ({ watch }: any) => `https://vts.linde.grp/Helium/VTS/Gas/Shipping.aspx?gtrkno=${watch('trackingNumberId')}`,
                        },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      items: [
                        {
                          // Legacy ddlGasModalBillingTrailerIn — all active containers; on change
                          // it sets the water-volume defaults (TrailerChange).
                          label: 'Container Started',
                          name: 'containerIdstarted',
                          type: FormItemType.select,
                          searchable: true,
                          options: trailerOptions('containerIdstarted', () => false),
                          reloadOnFields: ['plantTrackingNumberId'],
                          onChange: ({ getValues, setValue }: any) => applyTrailerChange(getValues, setValue),
                        },
                        {
                          // Legacy ddlGasModalBillingTrailerOut — the customer's on-site containers, or all with View All.
                          label: 'Container Ended',
                          name: 'containerIdended',
                          type: FormItemType.select,
                          searchable: true,
                          options: trailerOptions('containerIdended', (getValues: any) => !getValues('trailerOutViewAll')),
                          reloadOnFields: ['plantTrackingNumberId', 'trailerOutViewAll'],
                          onChange: ({ getValues, setValue }: any) => applyTrailerChange(getValues, setValue),
                        },
                        {
                          // Legacy cbGasModalBillingTrailerOutViewAll: rebuilds the list and reselects the default container.
                          label: 'View All',
                          name: 'trailerOutViewAll',
                          type: FormItemType.checkboxField,
                          onChange: ({ getValues, setValue }: any) => {
                            const def = getValues('defaultContainerId');
                            if (def) setValue('containerIdended', def);
                            applyTrailerChange(getValues, setValue);
                          },
                        },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      items: [
                        { label: 'Date/Time In', name: 'dateStarted', type: FormItemType.datetime },
                        { label: 'Date/Time Out', name: 'dateEnded', type: FormItemType.datetime },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      items: [
                        // Legacy takes PO/RO from the order page, not the modal.
                        { label: 'PO #', name: 'ponumber', disabled: () => true },
                        { label: 'Release #', name: 'roNumber', disabled: () => true },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      items: [
                        { label: 'Use Adjusted volume', name: 'jdeadjusted', type: FormItemType.checkboxField },
                        { label: 'Adjusted volume', name: 'jdeadjustedVolume', disabled: ({ watch }: any) => !watch('jdeadjusted') },
                      ],
                    },
                    { label: 'Delivery Comments', name: 'deliveryComments', type: FormItemType.textarea },
                    // Legacy tbGasModalBillingInstructions — read-only customer billing instructions.
                    { label: 'Billing Instructions', name: 'billingInstructions', type: FormItemType.textarea, disabled: () => true },
                    { name: 'billingType', defaultValue: 'GASBILLING', hideIf: () => true },
                    { name: 'productType', defaultValue: 'Gas', hideIf: () => true },
                  ],
                },
                                {
                  type: FormItemType.fieldset,
                  label: 'Invoice Contents',
                  items: [
                    {
                      type: FormItemType.row,
                      items: [
                        // Legacy lblNumTubes / lblTotalWaterVolume (Trailer In).
                        { label: 'Number of Tubes', name: 'numberOfTubes', type: FormItemType.labelField },
                        { label: 'Total Water Volume', name: 'totalWaterVolume', type: FormItemType.labelField },
                        ...[
                          { label: 'Tube Start Volume', name: 'tubeVolumeStartTotal' },
                          { label: 'Tube End Volume', name: 'tubeVolumeEndTotal' },
                          { label: 'Calculated Net Volume', name: 'tubeNetVolumeTotal' },
                        ].map((f) => ({
                          ...f,
                          type: FormItemType.labelField,
                          hideIf: ({ watch }: any) => !(watch('gasBillingDetails') ?? []).some(isTubeRow),
                        })),
                      ],
                    },
                    {
                      type: FormItemType.editTable,
                      name: 'gasBillingDetails',
                      variant: 'tabular',
                      pk: 'id',
                      // Tube By Tube: allow adding/removing bank rows; legacy pre-fills tube water volume per tube.
                      addTemplate: ({ getValues }: any) => ({
                        bank: null,
                        hePercentage: null,
                        pressureStarted: 0, tempStarted: 0, waterVolumeStarted: getValues?.('tubeWaterVolumeStarted') ?? 0,
                        pressureEnded: 0, tempEnded: 0, waterVolumeEnded: getValues?.('tubeWaterVolumeEnded') ?? 0,
                        volumeCalcStart: 0, volumeCalcEnd: 0, volumeCalcNet: 0,
                      }),
                      toolbar: [
                        {
                          type: FormItemType.button,
                          label: 'Use Plant Volumes (Starting)',
                          handler: async ({ getValues, setValue }: any) => applyPlantVolumes('Started', getValues, setValue),
                        },
                        {
                          type: FormItemType.button,
                          label: 'Use Plant Volumes (Ending)',
                          handler: async ({ getValues, setValue }: any) => applyPlantVolumes('Ended', getValues, setValue),
                        },
                        {
                          type: FormItemType.button,
                          label: 'Fill Sheet (Starting)',
                          handler: ({ getValues }: any) => openFillSheet('Started', getValues),
                        },
                        {
                          type: FormItemType.button,
                          label: 'Fill Sheet (Ending)',
                          handler: ({ getValues }: any) => openFillSheet('Ended', getValues),
                        },
                        {
                          type: FormItemType.button,
                          label: 'Recalculate Net',
                          handler: async ({ getValues, setValue }: any) =>
                            applyRecalced(await recalcInvoice(getValues()), getValues, setValue),
                        },
                      ],
                      columns: [
                        {
                          Header: 'Bank',
                          accessor: 'bank',
                          fixedWidth: 90,
                          formItemProps: {
                            name: 'bank', type: FormItemType.select, defaultValue: null, options: bankOptions, minWidth: 70,
                            // Tube rows calculate at 100% He and feed the tube totals.
                            onChange: ({ getValues, setValue, editTableProps, evt }: any) => {
                              if (setValue && editTableProps?.rowIdentity != null)
                                recalcRow('bank', evt?.target?.value, getValues, setValue, editTableProps.rowIdentity);
                            },
                          },
                        },
                        {
                          Header: 'He %',
                          accessor: 'hePercentage',
                          formItemProps: {
                            name: 'hePercentage',
                            number: true,
                            // Legacy shows He% as a read-only label (inventory He%, or 100% for tubes).
                            disabled: () => true,
                            className: 'readonly',
                            validation: {
                              minMax: {
                                message: 'He Percentage must be between 0 and 1',
                                handler: (val: string, getValues: any, existing: any, row: any) => {
                                  const { gasBillingDetails } = getValues();
                                  if (!gasBillingDetails[row]) return true;
                                  const { hePercentage } = gasBillingDetails[row];
                                  return hePercentage >= 0 && hePercentage <= 1;
                                },
                              },
                            },
                            onChange: (v: any, getValues: any, setValue: any, editTableProps: any) =>
                              recalcRow('hePercentage', v, getValues, setValue, editTableProps.rowIdentity),
                          },
                        },
                        readingColumn('Pressure Start', 'pressureStarted'),
                        readingColumn('Temp Start', 'tempStarted'),
                        readingColumn('Volume Start', 'waterVolumeStarted'),
                        calcColumn('Volume', 'volumeCalcStart'),
                        readingColumn('Pressure End', 'pressureEnded'),
                        readingColumn('Temp End', 'tempEnded'),
                        readingColumn('Volume End', 'waterVolumeEnded'),
                        calcColumn('Volume', 'volumeCalcEnd'),
                        calcColumn('Net', 'volumeCalcNet'),
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
} as FormView;

export const [GasBillingInvoiceCreateForOrder, GasBillingInvoiceCreateForOrderRepo] =
  connectView({ form: getGasBillingForOrderCreate, dataSource: gasBillingOrderDeliveryDataSource as DataSource });
